import { Component, Inject, ViewChild, ElementRef, Renderer2, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ManagementMediumClippingControllerService, ManagementMediumResponse, ManagementTagClippingControllerService, ManagementTagResponse } from 'api-clipping';
import { UtilsService } from '../../../../../shared/services/utils.service';
import { take } from 'rxjs/internal/operators/take';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CommonModule } from '@angular/common';
import { startWith } from 'rxjs/internal/operators/startWith';
import { debounceTime } from 'rxjs/internal/operators/debounceTime';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'app-clips-tools',
	standalone: true,
	imports: [
		ReactiveFormsModule,
		FormsModule,
		MatIconModule,
		MatButtonModule,
		MatDividerModule,
		MatSelectModule,
		TranslateModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatDialogModule,
		MatCheckboxModule,
		MatAutocompleteModule,
		CommonModule,
	],
	templateUrl: './clips-tools.component.html',
	styleUrl: './clips-tools.component.scss',
})
export class ClipsToolsComponent implements OnDestroy {
	@ViewChild('customDropdownTrigger') customDropdownTrigger!: ElementRef;
	
	formGroup: FormGroup = new FormGroup({});

	tools = ['MERGE_SIMILAR', 'AUTO_TAG', 'PDF_BY_TAG'];

	dateRangePicker: FormGroup = new FormGroup({
		dateFrom: new FormControl(),
		dateTo: new FormControl(),
	});

	filteredMediums: ManagementMediumResponse[] = [];
	filteredTags: ManagementTagResponse[] = [];
	allTags: ManagementTagResponse[] = [];
	
	// Custom dropdown properties
	customDropdownOpen = false;
	customSearchTerm = '';
	customFilteredTags: ManagementTagResponse[] = [];
	private dropdownPanel: HTMLElement | null = null;
	private documentClickListener?: () => void;

	fieldsToDisplay: any[] = [];

	constructor(
		public dialogRef: MatDialogRef<ClipsToolsComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private fb: FormBuilder,
		public utils: UtilsService,
		private apiClippingMedium: ManagementMediumClippingControllerService,
		private apiClippingTag: ManagementTagClippingControllerService,
		private renderer: Renderer2,
		private translateService: TranslateService
	) {
		if (data && data.type === 'custom-fields') {
			this.data.fields.forEach((field: any) => {
				switch (field.type) {
					case 'date-range': {
						this.dateRangePicker.controls['dateFrom'].setValue(new Date(field.value[0].setHours(0, 0, 0)));
						this.dateRangePicker.controls['dateTo'].setValue(new Date(field.value[1].setHours(0, 0, 0)));
						break;
					}
				}
			});
		}

		this.initializeForm();
		this.loadAllTags();
	}

	initializeForm() {
		this.formGroup = this.fb.group({
			tool: [, [Validators.required]],
			source: [],
			medium: [],
			tags: [],
		});

		this.formGroup.controls['medium'].valueChanges.pipe(startWith(''), debounceTime(450), distinctUntilChanged(), takeUntilDestroyed()).subscribe(searchTerm => {
			this.searchMediums(searchTerm);
		});


	}

	onSelectTool() {
		switch (this.formGroup.controls['tool'].value) {
			case 'MERGE_SIMILAR': {
				this.fieldsToDisplay = this.data.fields.filter((item: any) => item.type === 'date-range' || item.type === 'tag-selection');
				break;
			}
			case 'AUTO_TAG': {
				this.fieldsToDisplay = this.data.fields.filter((item: any) => item.type === 'date-range' || item.type === 'tag-selection');
				break;
			}
			case 'PDF_BY_TAG': {
				this.fieldsToDisplay = this.data.fields.filter((item: any) => item.type === 'date-range' || item.type === 'medium-selection');
				break;
			}
		}
	}

	public buttonAction(data: any): void {
		switch (this.data.type) {
			case 'custom-fields': {
				data = this.formGroup.value;
				data.dateFrom = this.dateRangePicker.controls['dateFrom'].value;
				data.dateTo = this.dateRangePicker.controls['dateTo'].value;
				data.dateTo = new Date(new Date(data.dateTo).setDate(new Date(data.dateTo).getDate() + 1));
				break;
			}
		}
    this.dialogRef.close({ data });
	}

	closeDialog() {
		this.dialogRef.close();
	}

	/** Filter input medium name */
	private searchMediums(searchTerm: string) {
		if (searchTerm && searchTerm.length >= 1) {
			this.apiClippingMedium
				.searchMediums(searchTerm)
				.pipe(take(1))
				.subscribe({
					next: response => {
						this.filteredMediums = response;
					},
				});
		}
	}
	displayFn(medium: ManagementMediumResponse): string {
		return medium && medium.id ? this.utils.getParameterByLanguageScript(medium, 'name') : '';
	}

	selectAllTags() {
		const currentTags = this.formGroup.controls['tags'].value || [];
		const tagsToAdd = this.filteredTags.filter(tag => 
			!currentTags.find((selectedTag: any) => selectedTag.id === tag.id)
		);
		this.formGroup.controls['tags'].setValue([...currentTags, ...tagsToAdd]);
	}

	unselectAllTags() {
		const currentTags = this.formGroup.controls['tags'].value || [];
		const filteredTagIds = this.filteredTags.map(tag => tag.id);
		const remainingTags = currentTags.filter((tag: any) => 
			!filteredTagIds.includes(tag.id)
		);
		this.formGroup.controls['tags'].setValue(remainingTags);
	}

	loadAllTags() {
		this.apiClippingTag
			.searchTags('') // Empty search returns all tags
			.pipe(take(1))
			.subscribe({
				next: response => {
					console.log('Loaded tags:', response);
					this.allTags = response;
					this.filteredTags = response;
					this.customFilteredTags = response;
					this.formGroup.controls['tags'].setValue([]);
				},
				error: (error) => {
					console.error('Error loading tags:', error);
				}
			});
	}

	hasTagSelection(): boolean {
		return this.formGroup.controls['tool'].value === 'MERGE_SIMILAR' || this.formGroup.controls['tool'].value === 'AUTO_TAG';
	}

	getTagDisplayText(tag: any): string {
		if (!tag) {
			return '';
		}

		return this.utils.getParameterByLanguageScript(tag, 'name', true) || tag.value;
	}

	isTagSelected(tag: any): boolean {
		const currentTags = this.formGroup.controls['tags'].value || [];
		return currentTags.some((t: any) => t.id === tag.id);
	}

	getSelectedTagsDisplay(): string {
		const selectedTags = this.formGroup.controls['tags'].value || [];
		if (selectedTags.length === 0) return '';
		
		if (selectedTags.length <= 10) {
			return selectedTags.map((tag: any) => this.getTagDisplayText(tag)).join(', ');
		}
		
		return `${selectedTags.length} селектирани`;
	}

	// Custom dropdown methods
	toggleCustomDropdown(event: Event): void {
		event.stopPropagation();
		this.customDropdownOpen = !this.customDropdownOpen;
		
		if (this.customDropdownOpen) {
			this.customSearchTerm = '';
			this.customFilteredTags = this.allTags;
			this.createDropdownPanel();
			this.addDocumentClickListener();
		} else {
			this.closeDropdown();
		}
	}

	private createDropdownPanel(): void {
		if (!this.customDropdownTrigger) return;

		const triggerRect = this.customDropdownTrigger.nativeElement.getBoundingClientRect();
		
		this.dropdownPanel = this.renderer.createElement('div');
		this.renderer.addClass(this.dropdownPanel, 'custom-dropdown-panel');
		
		// Position the panel
		this.renderer.setStyle(this.dropdownPanel, 'top', `${triggerRect.bottom + 2}px`);
		this.renderer.setStyle(this.dropdownPanel, 'left', `${triggerRect.left}px`);
		this.renderer.setStyle(this.dropdownPanel, 'width', `${triggerRect.width}px`);
		
		// Create content
		this.createDropdownContent();
		
		// Append to body
		this.renderer.appendChild(document.body, this.dropdownPanel);
	}

	private createDropdownContent(): void {
		if (!this.dropdownPanel) return;

		// Search header
		const searchHeader = this.renderer.createElement('div');
		this.renderer.addClass(searchHeader, 'search-header');

		const searchInput = this.renderer.createElement('input');
		this.renderer.addClass(searchInput, 'custom-search-input');
		this.renderer.setAttribute(searchInput, 'type', 'text');
		this.renderer.setAttribute(searchInput, 'placeholder', this.translateService.instant('BUTTON.SEARCH'));
		this.renderer.listen(searchInput, 'input', (e) => {
			this.customSearchTerm = e.target.value;
			this.onCustomSearch();
			this.updateDropdownContent();
		});
		this.renderer.listen(searchInput, 'click', (e) => {
			e.stopPropagation();
		});

		this.renderer.appendChild(searchHeader, searchInput);
		this.renderer.appendChild(this.dropdownPanel, searchHeader);
		setTimeout(() => searchInput.focus());

		// Options container
		const optionsContainer = this.renderer.createElement('div');
		this.renderer.addClass(optionsContainer, 'options-container');
		this.renderer.appendChild(this.dropdownPanel, optionsContainer);

		this.updateDropdownContent();
	}

	private updateDropdownContent(): void {
		if (!this.dropdownPanel) return;

		const optionsContainer = this.dropdownPanel.querySelector('.options-container');
		if (!optionsContainer) return;

		// Clear existing options
		optionsContainer.innerHTML = '';

		// Add filtered options
		if (this.customFilteredTags.length === 0) {
			const noResults = this.renderer.createElement('div');
			this.renderer.addClass(noResults, 'no-results');
			this.renderer.appendChild(noResults, this.renderer.createText('No tags found'));
			this.renderer.appendChild(optionsContainer, noResults);
		} else {
			this.customFilteredTags.forEach(tag => {
				const option = this.renderer.createElement('div');
				this.renderer.addClass(option, 'custom-option');
				
				const checkbox = this.renderer.createElement('input');
				this.renderer.setAttribute(checkbox, 'type', 'checkbox');
				this.renderer.setProperty(checkbox, 'checked', this.isTagSelected(tag));
				this.renderer.listen(checkbox, 'click', (e) => {
					e.stopPropagation();
				});
				
				const label = this.renderer.createElement('label');
				this.renderer.appendChild(label, this.renderer.createText(this.getTagDisplayText(tag)));
				
				this.renderer.appendChild(option, checkbox);
				this.renderer.appendChild(option, label);
				
				this.renderer.listen(option, 'click', (e) => {
					e.stopPropagation();
					this.toggleCustomTagSelection(tag);
					this.updateDropdownContent();
				});
				
				this.renderer.appendChild(optionsContainer, option);
			});
		}
	}

	private addDocumentClickListener(): void {
		this.documentClickListener = () => {
			this.closeDropdown();
		};
		setTimeout(() => {
			document.addEventListener('click', this.documentClickListener!);
		}, 0);
	}

	private closeDropdown(): void {
		this.customDropdownOpen = false;
		if (this.dropdownPanel) {
			this.renderer.removeChild(document.body, this.dropdownPanel);
			this.dropdownPanel = null;
		}
		if (this.documentClickListener) {
			document.removeEventListener('click', this.documentClickListener);
			this.documentClickListener = undefined;
		}
	}

	onCustomSearch(): void {
		if (!this.customSearchTerm || this.customSearchTerm.length === 0) {
			this.customFilteredTags = this.allTags;
		} else {
			this.customFilteredTags = this.allTags.filter(tag =>
				this.getTagDisplayText(tag).toLowerCase().includes(this.customSearchTerm.toLowerCase())
			);
		}
	}

	toggleCustomTagSelection(tag: any): void {
		const currentTags = this.formGroup.controls['tags'].value || [];
		const tagIndex = currentTags.findIndex((t: any) => t.id === tag.id);
		
		if (tagIndex >= 0) {
			currentTags.splice(tagIndex, 1);
		} else {
			currentTags.push(tag);
		}
		
		this.formGroup.controls['tags'].setValue([...currentTags]);
	}

	ngOnDestroy(): void {
		this.closeDropdown();
	}
}
